import { useState } from 'react';
import { useAppContext } from '@/components/utilities/AppContext';
import Logo from '@/components/ui/logo';
import QuizQuestion from './QuizQuestion';
import QuizEmailForm from './QuizEmailForm';
import QuizResult from './QuizResult';

const QuizBlock = ({
  quizId,
  quizTitle,
  emailFormTitle,
  emailFormSubtitle,
  firstNamePlaceholder,
  emailPlaceholder,
  consentText,
  submitButtonLabel,
  resultCongratulationTitle,
  resultCongratulationSubtitle,
  resultScoreLabel,
  quizQuestions,
  quizResultLevels
}) => {
  const questions = quizQuestions;
  const { locale, rootPath } = useAppContext();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [formData, setFormData] = useState({ firstName: '', email: '' });
  const [result, setResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalQuestions = questions?.length || 0;
  const emailStep = totalQuestions;
  const resultStep = totalQuestions + 1;

  const handleSelectAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleNext = () => {
    setStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const res = await fetch('/api/quiz-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quizId,
          pagePath: rootPath,
          locale: locale || 'cs',
          answers,
          firstName: formData.firstName,
          email: formData.email
        })
      });

      const data = await res.json();

      if (res.ok) {
        setResult(data);
        setStep(resultStep);

        // Send to LeadHub
        if (window.lhi) {
          window.lhi('identify', {
            email: formData.email,
            firstName: formData.firstName,
            subscribe: ['newsletters']
          });
          window.lhi('addTag', { type: quizId, score: data.score });
        }
      }
    } catch (error) {
      console.error('Quiz submit error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[calc(100vh-4rem)] w-[90vw] flex-col pt-4 pb-8 xl:w-(--container-width)">
      <div className="mb-14 flex justify-center">
        <Logo className="h-[20px] w-[114px] text-white" />
      </div>
      <h2 className="mb-8 text-center font-sans text-[30px] leading-[32.6px] font-medium">{quizTitle}</h2>

      <div className="mx-auto max-w-[500px]">
        {step < totalQuestions && questions[step] && (
          <QuizQuestion
            question={questions[step]}
            questionIndex={step}
            totalQuestions={totalQuestions}
            selectedAnswer={answers[questions[step].id]}
            onSelectAnswer={(value) => handleSelectAnswer(questions[step].id, value)}
            onBack={handleBack}
            onNext={handleNext}
          />
        )}

        {step === emailStep && (
          <QuizEmailForm
            emailFormTitle={emailFormTitle}
            emailFormSubtitle={emailFormSubtitle}
            firstNamePlaceholder={firstNamePlaceholder}
            emailPlaceholder={emailPlaceholder}
            consentText={consentText}
            submitButtonLabel={submitButtonLabel}
            formData={formData}
            onFormChange={setFormData}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        )}

        {step === resultStep && result && (
          <QuizResult
            result={result}
            congratulationTitle={resultCongratulationTitle}
            congratulationSubtitle={resultCongratulationSubtitle}
            scoreLabel={resultScoreLabel}
          />
        )}
      </div>
    </div>
  );
};

export default QuizBlock;
